<template>
  <div class="data-panel">
    <h3 class="data-title">Armazenamento</h3>
    <div v-if="!detail" style="color:#9ca3af;font-size:14px;">Carregando...</div>
    <template v-else>
      <div class="data-bar-wrap">
        <div
          class="data-bar-fill"
          :style="{ width: Math.min(100, (detail.usedMb / detail.storageLimitMb) * 100) + '%', background: storageColor(detail.usedMb, detail.storageLimitMb) }"
        />
      </div>
      <p class="data-bar-label">{{ formatMb(detail.usedMb) }} usados de {{ formatMb(detail.storageLimitMb) }} ({{ Math.round((detail.usedMb / detail.storageLimitMb) * 100) }}%)</p>

      <div v-if="breakdownItems.length" class="data-breakdown">
        <span v-for="item in breakdownItems" :key="item.key" class="data-bd-item">
          <span class="data-bd-dot" :style="{ background: item.color }"/>{{ item.label }}: {{ formatMb(item.mb) }}
        </span>
      </div>

      <div class="data-settings">
        <h4 class="data-settings-title">Configurações</h4>
        <div class="data-field">
          <label>Limite de armazenamento (MB)</label>
          <input v-model.number="editStorageLimitMb" type="number" min="100" step="100" />
        </div>
        <div class="data-field">
          <label>Data de renovação anual</label>
          <input v-model="editRenewalDate" type="date" />
        </div>
        <p v-if="saveError" class="data-error">{{ saveError }}</p>
        <button class="btn-admin-primary" type="button" :disabled="saving" @click="saveSettings">
          {{ saving ? 'Salvando...' : 'Salvar configurações' }}
        </button>
      </div>
    </template>
  </div>
</template>

<script lang="ts">
import { computed, ref, inject, onMounted, watch } from 'vue'
import { masterFetch } from '@/store/masterSession'
import { breakdownToItems } from '@/utils/storageCategories'

export default {
  name: 'AdminSysDataTab',
  setup() {
    const detail = inject<any>('adminDetail')
    const refreshDetail = inject<() => Promise<void>>('adminDetailRefresh')

    const editStorageLimitMb = ref(500)
    const editRenewalDate = ref('')
    const saving = ref(false)
    const saveError = ref('')

    const initForm = () => {
      if (detail && detail.value) {
        editStorageLimitMb.value = detail.value.storageLimitMb
        editRenewalDate.value = detail.value.renewalDate ? detail.value.renewalDate.slice(0, 10) : ''
      }
    }

    onMounted(initForm)
    watch(() => detail?.value, initForm)

    const breakdownItems = computed(() => breakdownToItems(detail?.value?.usageBreakdown))

    const formatMb = (mb: number) => {
      if (mb >= 1024) return (mb / 1024).toFixed(2) + ' GB'
      return mb.toFixed(1) + ' MB'
    }

    const storageColor = (used: number, limit: number) => {
      const pct = limit > 0 ? used / limit : 0
      if (pct >= 0.9) return '#dc2626'
      if (pct >= 0.7) return '#f59e0b'
      return '#16a34a'
    }

    const saveSettings = async () => {
      saveError.value = ''
      saving.value = true
      try {
        await masterFetch(`/master/systems/${detail.value?.systemId}/settings`, {
          method: 'PUT',
          body: JSON.stringify({ storageLimitMb: editStorageLimitMb.value, renewalDate: editRenewalDate.value })
        })
        if (detail.value) {
          detail.value.storageLimitMb = editStorageLimitMb.value
          detail.value.renewalDate = editRenewalDate.value
        }
      } catch (err: unknown) {
        saveError.value = err instanceof Error ? err.message : 'Erro ao salvar.'
      } finally {
        saving.value = false
      }
    }

    return { detail, editStorageLimitMb, editRenewalDate, saving, saveError, formatMb, storageColor, saveSettings, breakdownItems }
  }
}
</script>

<style lang="scss">
.data-panel { padding: 28px; }
.data-title { font-size: 16px; font-weight: 600; color: #111827; margin: 0 0 14px; }

.data-bar-wrap {
  height: 10px;
  background: #e5e7eb;
  border-radius: 99px;
  overflow: hidden;
  margin-bottom: 6px;
}

.data-bar-fill { height: 100%; border-radius: 99px; transition: width 0.3s; }
.data-bar-label { font-size: 13px; color: #6b7280; margin: 0 0 10px; }

.data-breakdown { display: flex; flex-wrap: wrap; gap: 8px 18px; margin: 0 0 24px; }
.data-bd-item { display: inline-flex; align-items: center; gap: 6px; font-size: 12.5px; color: #4b5563; }
.data-bd-dot { width: 9px; height: 9px; border-radius: 50%; display: inline-block; }

.data-settings {
  border-top: 1px solid #e5e7eb;
  padding-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.data-settings-title { font-size: 15px; font-weight: 600; margin: 0 0 4px; }

.data-field {
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-width: 320px;

  label { font-size: 13px; font-weight: 600; color: #374151; }

  input, select, textarea {
    padding: 9px 12px;
    border: 1.5px solid #d1d5db;
    border-radius: 8px;
    font-size: 14px;
    font: inherit;
    outline: none;
    &:focus { border-color: #0f4c81; }
  }
}

.data-error {
  color: #dc2626;
  font-size: 13px;
  background: #fef2f2;
  padding: 8px 12px;
  border-radius: 6px;
  margin: 0;
}

.data-success {
  color: #166534;
  font-size: 13px;
  background: #dcfce7;
  padding: 8px 12px;
  border-radius: 6px;
  margin: 0;
}

.btn-admin-primary {
  background: #0f4c81;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 9px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  align-self: flex-start;

  &:hover:not(:disabled) { background: #0d3d6a; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
}

.data-sep {
  border: none;
  border-top: 1px solid #e5e7eb;
  margin: 24px 0;
}

.data-select {
  padding: 9px 12px;
  border: 1.5px solid #d1d5db;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  background: #fff;
  max-width: 320px;
  font: inherit;
  &:focus { border-color: #0f4c81; }
}
</style>
